import type React from "react"

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";

import FolderOutlined from "@mui/icons-material/FolderOutlined";
import InsertDriveFileOutlined from "@mui/icons-material/InsertDriveFileOutlined";
import CalendarToday from "@mui/icons-material/CalendarToday";
import Tag from "@mui/icons-material/Tag";
import Person from "@mui/icons-material/Person";
import Edit from "@mui/icons-material/Edit";

import type { TreeNode, FolderStatus, FileStatus } from "../../file-tree-manager/types/tree-types"
import { formatFileSize, formatDate } from "../../file-tree-manager/utils/format-utils"
import { ContentsManager } from "./contents-manager"
import { AttachmentManager } from "../../file-tree-manager/components/attachment-manager"
import { StatusManager } from "../../file-tree-manager/components/status-manager"
import { useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import { setMenu } from "../../reducer/menu"
import { openAlert } from '../../reducer/alert'
import { store } from '../../store/AriStore'

interface PropertiesPanelProps {
  selectedNode: TreeNode | null
  treeData: TreeNode[]
  onUpdateContentsManager?: (nodeId: string, contentsManager: string[]) => void
  onUpdateUrl?: (nodeId: string, url: string) => void
  onUpdateStatus?: (nodeId: string, status: FolderStatus | FileStatus) => void
  onAddAttachment?: (nodeId: string, attachment: any) => void
  onRemoveAttachment?: (nodeId: string, attachmentId: string) => void
}

export const PropertiesPanel: React.FC<PropertiesPanelProps> = ({
  selectedNode,
  treeData,
  onUpdateContentsManager,
  onUpdateUrl,
  onUpdateStatus,
  onAddAttachment,
  onRemoveAttachment,
}) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  if (!selectedNode) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          color: "text.secondary",
        }}
      >
        <FolderOutlined sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
        <Typography variant="body1">항목을 선택하여 속성을 확인하세요</Typography>
      </Box>
    )
  }

  const handleUpdateContentsManager = (contentsManager: string[]) => {
    if (onUpdateContentsManager && selectedNode) {
      onUpdateContentsManager(selectedNode.id, contentsManager)
    }
  }

  const handleUpdateUrl = (url: string) => {
    if (onUpdateUrl && selectedNode) {
      onUpdateUrl(selectedNode.id, url)
    }
  }

  const handleUpdateStatus = (status: FolderStatus | FileStatus) => {
    if(selectedNode.type === "file" && selectedNode.attachments?.length == 0) {
      store.dispatch(openAlert({ message: '등록된 파일이 없습니다.\n첨부 파일 등록 후, 변환 요청을 진행해주세요.' }))
      return;
    }

    if (onUpdateStatus && selectedNode) {
      onUpdateStatus(selectedNode.id, status)
    }
  }

  const handleAddAttachment = (attachment: any) => {
    if (onAddAttachment && selectedNode) {
      onAddAttachment(selectedNode.id, attachment)
    }
  }

  const handleRemoveAttachment = (attachmentId: string) => {
    if (onRemoveAttachment && selectedNode) {
      onRemoveAttachment(selectedNode.id, attachmentId)
    }
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", py: 2, position: "sticky", top: 0, zIndex: 1, bgcolor: "background.paper" }}>
        {selectedNode.type === "folder" ? (
          <FolderOutlined sx={{ fontSize: 32, mr: 2, color: "primary.main" }} />
        ) : (
          <InsertDriveFileOutlined sx={{ fontSize: 32, mr: 2, color: "text.secondary" }} />
        )}
        <Box>
          <Typography variant="h6" component="h3" sx={{ fontSize: 16, fontWeight: 700 }}>
            {selectedNode.title}
          </Typography>
          <Chip
            label={selectedNode.type === "folder" ? "부서" : "컨텐츠"}
            color={selectedNode.type === "folder" ? "primary" : "default"}
            size="small"
          />
        </Box>
        {selectedNode.type === "file" && selectedNode.status !== "draft" && selectedNode.status !== "converting" && selectedNode.status !== "convert" && (<Box sx={{ ml: "auto" }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              // 사이드바의 메뉴 상태가 라우팅을 강제하므로, 먼저 메뉴를 문서 관리(910)로 변경
              dispatch(setMenu({ index: 910, name: "문서 관리", welcome: "" }))
              navigate(`/console/document-manager?id=${encodeURIComponent(selectedNode.id)}`)
            }}
          >
            이동
          </Button>
        </Box>)}
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* 상태 관리 섹션 */}
      <StatusManager node={selectedNode} onStatusChange={handleUpdateStatus} />

      {/* URL 관리 섹션 */}
      {/* {selectedNode.type === "file" && (
        <UrlManager url={selectedNode.url} onUpdate={handleUpdateUrl} />
      )} */}

      {/* 컨텐츠 첨부 섹션 */}
      {selectedNode.type === "file" && (
      <AttachmentManager
        nodeId={selectedNode.id}
        attachments={selectedNode.attachments || []}
        onAdd={handleAddAttachment}
        onRemove={handleRemoveAttachment}
        />
      )}

      {/* 부서인 경우에만 관리자 섹션 표시 */}
      {selectedNode.type === "folder" && (
        <ContentsManager
          contentsManager={selectedNode.contentsManager || []}
          onUpdate={handleUpdateContentsManager}
        />
      )}

      {selectedNode.type === "file" && selectedNode.size !== undefined && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <Tag sx={{ mr: 1, color: "text.secondary" }} />
            <Typography variant="subtitle2">크기:</Typography>
          </Box>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: "grey.50" }}>
            <Typography variant="body2">{formatFileSize(selectedNode.size)}</Typography>
          </Paper>
        </Box>
      )}

      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <CalendarToday sx={{ mr: 1, color: "text.secondary" }} />
          <Typography variant="subtitle2">날짜 정보:</Typography>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {selectedNode.createdAt && (
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2" color="text.secondary">
                생성일:
              </Typography>
              <Typography variant="body2">{formatDate(selectedNode.createdAt)}</Typography>
            </Box>
          )}
          {selectedNode.modifiedAt && (
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2" color="text.secondary">
                수정일:
              </Typography>
              <Typography variant="body2">{formatDate(selectedNode.modifiedAt)}</Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* 작성자/수정자 정보 섹션 */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Person sx={{ mr: 1, color: "text.secondary" }} />
          <Typography variant="subtitle2">작성자 정보:</Typography>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {selectedNode.author && (
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="body2" color="text.secondary">
                작성자:
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Chip label={selectedNode.author} size="small" color="primary" variant="outlined" />
              </Box>
            </Box>
          )}
          {selectedNode.lastModifiedBy && (
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="body2" color="text.secondary">
                최종 수정자:
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Edit sx={{ fontSize: 16, color: "text.secondary" }} />
                <Chip label={selectedNode.lastModifiedBy} size="small" color="secondary" variant="outlined" />
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  )
}


